import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Building2, Mail, Phone, Search } from 'lucide-react';

const organismes = [
  { name: 'CAF', cat: 'Aides', accent: '#3b82f6' },
  { name: 'Impôts', cat: 'Fiscalité', accent: '#ef4444' },
  { name: 'CPAM / ameli', cat: 'Santé', accent: '#10b981' },
  { name: 'Pôle Emploi', cat: 'Emploi', accent: '#f59e0b' },
  { name: 'CROUS', cat: 'Étudiants', accent: '#8b5cf6' },
  { name: 'Mutuelle', cat: 'Santé', accent: '#06b6d4' },
  { name: 'Opérateur télécom', cat: 'Abonnements', accent: '#f97316' },
  { name: 'Banque', cat: 'Finances', accent: '#84cc16' },
  { name: 'Assurance', cat: 'Logement', accent: '#ec4899' },
  { name: 'Énergie (EDF, Engie…)', cat: 'Abonnements', accent: '#eab308' },
  { name: 'Propriétaire / Bailleur', cat: 'Logement', accent: '#14b8a6' },
  { name: 'Employeur', cat: 'Travail', accent: '#6366f1' },
];

export const OrganismesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const { isDark } = useTheme();

  return (
    <section
      id="organismes"
      className={`section-apple px-6 ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`}
      data-testid="organismes-section"
    >
      <div className="max-w-[1100px] mx-auto">
        {/* Title */}
        <motion.div
          ref={ref}
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <p className="text-[13px] tracking-[0.02em] uppercase mb-4 text-[#81d8d0]">
            La fonctionnalité différenciante
          </p>
          <h2
            className={`text-[40px] sm:text-[48px] font-semibold tracking-[-0.03em] leading-[1.05] mb-6 ${
              isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
            }`}
          >
            Tous tes organismes
            <br />
            <span className="text-[#81d8d0]">au même endroit.</span>
          </h2>
          <p
            className={`text-[17px] sm:text-[19px] leading-[1.5] max-w-[640px] mx-auto ${
              isDark ? 'text-[#86868b]' : 'text-[#6e6e73]'
            }`}
          >
            CAF, impôts, mutuelle, opérateur, assurance, banque… Skipya
            retrouve les contacts utiles, garde tes références client et
            te prépare les emails à envoyer.
          </p>
        </motion.div>

        {/* Mockup card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.15 }}
          className={`rounded-3xl p-6 sm:p-10 ${
            isDark ? 'bg-[#141414] border-white/5' : 'bg-[#f5f5f7] border-black/5'
          } border`}
        >
          {/* Fake toolbar */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/5">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#81d8d0]" />
              <span
                className={`text-[13px] font-medium ${
                  isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
                }`}
              >
                Mes organismes
              </span>
              <span
                className={`text-[11px] ${
                  isDark ? 'text-[#6e6e73]' : 'text-[#86868b]'
                }`}
              >
                {organismes.length} suivis
              </span>
            </div>
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12px] ${
                isDark ? 'bg-white/5 text-[#86868b]' : 'bg-white text-[#86868b]'
              }`}
            >
              <Search className="w-3 h-3" />
              Rechercher…
            </div>
          </div>

          {/* Grid of organismes */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {organismes.map((o, i) => (
              <motion.div
                key={o.name}
                initial={{ opacity: 0, y: 8 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.2 + i * 0.03 }}
                className={`p-3 rounded-xl ${
                  isDark ? 'bg-black/40' : 'bg-white'
                }`}
                data-testid={`org-${i}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ background: o.accent }}
                  />
                  <span
                    className={`text-[13px] font-medium truncate ${
                      isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
                    }`}
                  >
                    {o.name}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-[10px] opacity-60">
                  <span className="flex items-center gap-1">
                    <Mail className="w-2.5 h-2.5" />
                    contact
                  </span>
                  <span className="flex items-center gap-1">
                    <Phone className="w-2.5 h-2.5" />
                    tel
                  </span>
                </div>
                <div
                  className={`text-[10px] mt-1 uppercase tracking-widest ${
                    isDark ? 'text-[#3a3a3c]' : 'text-[#a1a1a6]'
                  }`}
                >
                  {o.cat}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Benefits row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8">
            {[
              { t: 'Mails prêts à envoyer', s: 'Skipya pré-rédige tes courriers selon l\'organisme.' },
              { t: 'Numéro client conservé', s: 'Plus besoin de fouiller dans ton boîte mail.' },
              { t: 'Horaires & contacts', s: 'Téléphone, email, espace en ligne en un clic.' },
            ].map((b, i) => (
              <div
                key={i}
                className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white/70'}`}
              >
                <div
                  className={`text-[13px] font-medium mb-0.5 ${
                    isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
                  }`}
                >
                  {b.t}
                </div>
                <div
                  className={`text-[11px] ${
                    isDark ? 'text-[#6e6e73]' : 'text-[#86868b]'
                  }`}
                >
                  {b.s}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default OrganismesSection;
